import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "motion/react";
import SEOHead from "@/components/SEOHead";
import VovyGuide, { VOVY_SRC, type GuideStop } from "@/components/desktop/VovyGuide";
import VovyNotch, { type NotchTheme } from "@/components/desktop/VovyNotch";
import AppleLogo from "@/components/icons/AppleLogo";
import { VOVY_DOES_STOPS } from "@/components/desktop/vovyDoesStops";
import WindowsWaitlistButton from "@/components/desktop/WindowsWaitlistButton";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import PricingSection from "@/components/desktop/PricingSection";
import HomeHero from "@/pages/HeroTest/HomeHero";
import DownloadCta from "@/components/desktop/DownloadCta";
import { MACOS_DOWNLOAD_URL } from "@/lib/desktopDownload";
import { useAuth } from "@/contexts/AuthContext";

const Footer = lazy(() => import("@/components/Footer"));
const VovyDoesSection = lazy(() => import("@/components/desktop/VovyDoesSection"));
const IntegrationsArc = lazy(() => import("@/components/desktop/IntegrationsArc"));

// Where the flying Vovy parks on its way down the page, in DOM order.
const GUIDE_STOPS: GuideStop[] = [
  { id: "integrations", large: true },
  ...VOVY_DOES_STOPS,
  { id: "pricing", rocket: true, large: true },
];

// What to know about me, privacy, and plans — asked of Vovy, answered by Vovy,
// the way the rest of this page speaks.
type FaqItem = { q: string; a: string; links?: [label: string, href: string][] };

// Written for founders and non-coders building with AI. Each answer names
// Vovy and stands on its own, so search engines and AI assistants can quote
// it without the question. Also emitted as FAQPage JSON-LD below.
const FAQ_ITEMS: FaqItem[] = [
  {
    q: "What is Vovy?",
    a: "Vovy is an AI assistant for Mac that lives in your notch and follows your cursor. It is a computer use agent: it can see your screen when you ask, explain what you are looking at, write better prompts, and do tasks for you in any app, like Lovable, Cursor, Claude, Supabase, or Vercel.",
  },
  {
    q: "What can Vovy do?",
    a: "Vovy can set up your Mac for coding, connect your app to GitHub, put it live on Vercel, set up a Supabase database, add sign-in, take payments with Stripe, run an Expo app in the iOS Simulator, and fix errors like a blank white screen or a failed build. Browse every task, with steps and what you get, in the Vovy task library.",
    links: [
      ["Browse all tasks", "/tasks"],
      ["Set up your Mac", "/tasks?category=setup"],
      ["Go live", "/tasks?category=deploy"],
      ["Fix errors", "/tasks?category=debug"],
      ["Payments", "/tasks?category=payments"],
    ],
  },
  {
    q: "Do I need to know how to code to use Vovy?",
    a: "No. Vovy is built for founders and beginners who build with AI tools. You say what you want in plain words, and Vovy explains each step, shows you where to click, or does the step for you while you watch.",
  },
  {
    q: "How do I start Vovy?",
    a: "Hold the Fn and Control keys together, or click Vovy in your Mac's notch, then speak or type. You can change the shortcut to any keys you like.",
  },
  {
    q: "Which AI tools does Vovy work with?",
    a: "Vovy works in any app on your Mac, including Lovable, Bolt, Cursor, Claude, ChatGPT, Replit, Rork, Figma, Notion, Terminal, and your browser. If you can build in it, Vovy can help.",
  },
  {
    q: "How is Vovy different from ChatGPT or Claude?",
    a: "ChatGPT and Claude answer in a chat window, so you copy and paste and describe your screen. Vovy sees the app you are in when you ask, points at the exact button, and can click and type for you, so you don't switch windows or explain your screen.",
  },
  {
    q: "Can Vovy fix errors when my AI-built app breaks?",
    a: "Yes. Vovy reads the error on your screen, explains it in plain words, and fixes common problems like a blank white screen, a failed deploy, CORS errors, missing environment variables, and Supabase permission errors.",
    links: [["See fix-it tasks", "/tasks?category=debug"]],
  },
  {
    q: "Is my data private with Vovy?",
    a: "Yes. Vovy only takes a screen snapshot in the moment you ask, never records continuously, and never saves screenshots to disk. Your projects stay on your Mac, and nothing you build is used to train AI models or sold.",
  },
  {
    q: "Will Vovy do anything without asking me?",
    a: "No. Vovy asks before any step it can't undo, like deleting files, spending money, or publishing your app, and you can stop it at any time.",
  },
  {
    q: "How much does Vovy cost?",
    a: "Vovy has a Free plan with 100 credits a month. Pro is $30 a month for 500 credits and Max is $100 a month for 2,000 credits. Yearly billing saves up to 33%, and you can cancel anytime.",
    links: [["See pricing", "/pricing"]],
  },
  {
    q: "Does Vovy work on Windows?",
    a: "Not yet. Vovy runs on macOS today. Join the Windows waitlist and we will email you when it is ready.",
  },
  {
    q: "Do I get Vovy Learn with my plan?",
    a: "Yes. Every Vovy plan, including Free, includes Vovy Learn: hands-on courses that teach you to build real apps with AI, plus prompt labs.",
    links: [["Explore Vovy Learn", "/learn"]],
  },
];

/* ── The hero desktop frame ──────────────────────────────────────────────
 * The first view IS a Mac running Vovy (adrien.website/txt framing): system
 * menu bar with the real  glyph and SF stack, the live notch hanging where
 * it lives, the pitch on a glass panel, and a dock holding exactly one app.
 * Scrolling shrinks the desktop into a rounded frame and hands off to the
 * page below. */

const MENU_FONT =
  "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif";

/** Dock apps flanking Vovy — the AI tools it works inside. Each is the app's
 *  own macOS icon (or the vendor's official mark), stored in
 *  public/images/dock and public/images/tools, not fetched from a logo
 *  service at view time. */
type DockApp = { label: string; className: string; src: string; pad?: string };

const DOCK_LEFT: DockApp[] = [
  { label: "Cursor", className: "bg-transparent", src: "/images/dock/cursor.png" },
  { label: "Lovable", className: "bg-transparent", src: "/images/tools/lovable.png" },
  { label: "Bolt", className: "bg-transparent", src: "/images/tools/bolt.png" },
  { label: "ChatGPT", className: "bg-transparent", src: "/images/dock/chatgpt.png" },
];

const DOCK_RIGHT: DockApp[] = [
  { label: "Claude", className: "bg-transparent", src: "/images/dock/claude.png" },
  { label: "Replit", className: "bg-white", src: "/images/tools/replit.png", pad: "p-[3px]" },
  { label: "Figma", className: "bg-transparent", src: "/images/dock/figma.png" },
  { label: "Notion", className: "bg-transparent", src: "/images/dock/notion.png" },
];

const DockIcon = ({ app }: { app: DockApp }) => (
  <div
    title={app.label}
    className={`hidden h-[34px] w-[34px] items-center justify-center overflow-hidden rounded-[9px] shadow-[0_2px_6px_rgba(2,32,58,0.25)] sm:flex sm:h-[40px] sm:w-[40px] sm:rounded-[10px] ${app.className}`}
  >
    <img
      src={app.src}
      alt=""
      loading="lazy"
      className={`h-full w-full rounded-[8px] object-contain ${app.pad ?? ""}`}
    />
  </div>
);

/** Tiny avatar for the menu bar: photo if we have one, initials otherwise. */
const HeroAvatar = ({ url, name }: { url: string | null; name: string }) => {
  const [broken, setBroken] = useState(false);
  if (url && !broken) {
    return (
      <img
        src={url}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        className="h-full w-full object-cover"
      />
    );
  }
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "U";
  return (
    <span className="flex h-full w-full items-center justify-center bg-[#0EA5E9] text-[10px] font-semibold leading-none text-white">
      {initials}
    </span>
  );
};

/** Live menu-bar clock, macOS format: "Mon Aug 31  3:49 PM". */
const useMenuClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const date = now.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const time = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${date}  ${time}`;
};

// THE MAC DESKTOP, REUSED (owner, 2026-09-14): /desktop/installing shows this
// same desktop — menu bar, notch, wallpaper, dock, the scroll-shrink — with the
// installer window where the headline and button sit. `content` swaps the
// centre block; `homeLinks` points the section links back at the home page.
export const HeroDesktopFrame = ({ theme, content, homeLinks = false }: { theme: NotchTheme; content?: ReactNode; homeLinks?: boolean }) => {
  const base = homeLinks ? "/" : "";
  const wrapRef = useRef<HTMLDivElement>(null);
  // 0 at the top of the page, 1 when the tall wrapper has scrolled past:
  // the desktop shrinks into a rounded frame over the first ~60% of that.
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end end"],
  });
  const scale = useTransform(scrollYProgress, [0, 0.6], [1, 0.9]);
  const radius = useTransform(scrollYProgress, [0, 0.6], [0, 28]);
  const clock = useMenuClock();
  // Mobile-only dropdown menu (the bar's items collapse into it below sm).
  const [menuOpen, setMenuOpen] = useState(false);

  const sectionRef = useRef<HTMLElement>(null);
  const [appleOpen, setAppleOpen] = useState(false);

  // Signed-in users get their avatar (→ dashboard) in place of Sign in, so a
  // returning user is never bounced through /signin → onboarding.
  const { user, loading: authLoading, getUserAvatar, getUserDisplayName } = useAuth();
  const auth = {
    loading: authLoading,
    signedIn: !!user,
    avatarUrl: getUserAvatar(),
    displayName: getUserDisplayName(),
  };

  return (
    <div ref={wrapRef} className="relative h-[175vh]">
      <motion.section
        ref={sectionRef}
        data-vovy-hero
        style={{ scale, borderRadius: radius }}
        className="sticky top-0 h-screen overflow-hidden"
      >
        {/* The wallpaper — the painterly sky art, back in its natural role. */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom, #37A2E6, #7FD2F2)" }}
        />
        <picture>
          <source media="(min-width: 640px)" srcSet="/images/desktopbg.jpg" />
          <img
            src="/images/phonebg.jpg"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover object-center"
          />
        </picture>

        {/* System menu bar: real  glyph, SF stack, nav as menus, live
            clock. No bar chrome at all — the items sit straight on the
            wallpaper, the way a menu bar reads over a bright desktop. */}
        <div
          className="absolute inset-x-0 top-0 z-40 flex h-[34px] items-center justify-between px-3 text-[14px] font-medium text-white sm:px-4"
          style={{ fontFamily: MENU_FONT, textShadow: "0 1px 6px rgba(2,32,58,0.25)" }}
        >
          <div className="flex items-center gap-4 sm:gap-5">
            {/* The  menu actually drops down. */}
            <button
              type="button"
              aria-label="Apple menu"
              aria-expanded={appleOpen}
              onClick={() => setAppleOpen((v) => !v)}
              className="-mx-1.5 flex h-6 items-center rounded-md px-1.5 transition-colors hover:bg-white/20"
            >
              <AppleLogo className="h-[14px] w-[14px]" />
            </button>
            <span className="hidden font-semibold sm:inline">Vovy</span>
            <a href={`${base}#features`} className="hidden opacity-85 transition-opacity hover:opacity-100 sm:inline">
              Features
            </a>
            <a href={`${base}#pricing`} className="hidden opacity-85 transition-opacity hover:opacity-100 sm:inline">
              Pricing
            </a>
            <a href={`${base}#faq`} className="hidden opacity-85 transition-opacity hover:opacity-100 md:inline">
              FAQ
            </a>
            <Link to="/learn" className="hidden opacity-85 transition-opacity hover:opacity-100 md:inline">
              Learn
            </Link>
          </div>
          <div className="flex items-center gap-4">
            {/* Nothing renders while auth resolves so the Sign in pill
                doesn't flash for returning users. */}
            {!auth.loading &&
              (auth.signedIn ? (
                <Link
                  to="/app/home"
                  aria-label="Open dashboard"
                  title={auth.displayName}
                  className="hidden h-[22px] w-[22px] items-center justify-center overflow-hidden rounded-full ring-1 ring-inset ring-white/60 shadow-[0_1px_4px_rgba(2,32,58,0.3)] transition-transform hover:scale-105 sm:inline-flex"
                >
                  <HeroAvatar url={auth.avatarUrl} name={auth.displayName} />
                </Link>
              ) : (
                <Link
                  to="/signin"
                  className="hidden items-center gap-1.5 rounded-full bg-white/25 px-3 py-[3px] ring-1 ring-inset ring-white/40 backdrop-blur-sm transition-colors hover:bg-white/40 sm:inline-flex"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[13px] w-[13px] fill-current">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-3.6 3.6-6 8-6s8 2.4 8 6v1H4v-1z" />
                  </svg>
                  Sign in
                </Link>
              ))}
            <span className="hidden tabular-nums sm:inline">{clock}</span>
            {/* Mobile: the menus collapse into one macOS-style dropdown. */}
            <button
              type="button"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-white/20 sm:hidden"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[18px] w-[18px] fill-none stroke-current" strokeWidth="2" strokeLinecap="round">
                {menuOpen ? (
                  <>
                    <path d="M6 6l12 12" />
                    <path d="M18 6L6 18" />
                  </>
                ) : (
                  <>
                    <path d="M4 7h16" />
                    <path d="M4 12h16" />
                    <path d="M4 17h16" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* The  menu: frosted macOS panel under the bar. */}
        {appleOpen && (
          <div
            className="absolute left-2 top-[38px] z-50 w-56 overflow-hidden rounded-xl border border-white/60 bg-white/90 py-1.5 shadow-[0_18px_50px_-12px_rgba(2,32,58,0.45)] backdrop-blur-xl"
            style={{ fontFamily: MENU_FONT }}
          >
            <a
              href={`${base}#features`}
              onClick={() => setAppleOpen(false)}
              className="block px-4 py-2 text-[14px] font-medium text-[#1D1D1F] transition-colors hover:bg-black/[0.05]"
            >
              About Vovy
            </a>
            <Link
              to="/learn"
              onClick={() => setAppleOpen(false)}
              className="block px-4 py-2 text-[14px] font-medium text-[#1D1D1F] transition-colors hover:bg-black/[0.05]"
            >
              Vovy Learn
            </Link>
            <div className="mx-3 my-1.5 h-px bg-black/10" />
            <a
              href={MACOS_DOWNLOAD_URL}
              onClick={() => setAppleOpen(false)}
              className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-[#1D1D1F] transition-colors hover:bg-black/[0.05]"
            >
              <AppleLogo className="h-[13px] w-[13px]" />
              Download for macOS
            </a>
            <div className="mx-3 my-1.5 h-px bg-black/10" />
            {auth.signedIn ? (
              <Link
                to="/app/home"
                onClick={() => setAppleOpen(false)}
                className="flex items-center gap-2 px-4 py-2 text-[14px] font-semibold text-[#0B72B5] transition-colors hover:bg-black/[0.05]"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center overflow-hidden rounded-full">
                  <HeroAvatar url={auth.avatarUrl} name={auth.displayName} />
                </span>
                Dashboard
              </Link>
            ) : (
              <Link
                to="/signin"
                onClick={() => setAppleOpen(false)}
                className="block px-4 py-2 text-[14px] font-semibold text-[#0B72B5] transition-colors hover:bg-black/[0.05]"
              >
                Sign in
              </Link>
            )}
          </div>
        )}
        {appleOpen && (
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setAppleOpen(false)}
            className="absolute inset-0 z-[45] cursor-default"
          />
        )}

        {/* Mobile dropdown, styled like a macOS menu: frosted panel under the
            bar, dark ink, Sign in as its own row. */}
        {menuOpen && (
          <div
            className="absolute right-2 top-[38px] z-50 w-52 overflow-hidden rounded-xl border border-white/60 bg-white/90 py-1.5 shadow-[0_18px_50px_-12px_rgba(2,32,58,0.45)] backdrop-blur-xl sm:hidden"
            style={{ fontFamily: MENU_FONT }}
          >
            {[
              { label: "Features", href: `${base}#features` },
              { label: "Pricing", href: `${base}#pricing` },
              { label: "FAQ", href: `${base}#faq` },
            ].map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                className="block px-4 py-2 text-[14px] font-medium text-[#1D1D1F] transition-colors hover:bg-black/[0.05]"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/learn"
              onClick={() => setMenuOpen(false)}
              className="block px-4 py-2 text-[14px] font-medium text-[#1D1D1F] transition-colors hover:bg-black/[0.05]"
            >
              Learn
            </Link>
            <div className="mx-3 my-1.5 h-px bg-black/10" />
            {auth.signedIn ? (
              <Link
                to="/app/home"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-2 text-[14px] font-semibold text-[#0B72B5] transition-colors hover:bg-black/[0.05]"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center overflow-hidden rounded-full">
                  <HeroAvatar url={auth.avatarUrl} name={auth.displayName} />
                </span>
                Dashboard
              </Link>
            ) : (
              <Link
                to="/signin"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-2 text-[14px] font-semibold text-[#0B72B5] transition-colors hover:bg-black/[0.05]"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[14px] w-[14px] fill-current">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 20c0-3.6 3.6-6 8-6s8 2.4 8 6v1H4v-1z" />
                </svg>
                Sign in
              </Link>
            )}
          </div>
        )}
        {/* invisible scrim: tap anywhere else to close */}
        {menuOpen && (
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 z-[45] cursor-default sm:hidden"
          />
        )}

        {/* The notch — the product, hanging flared and flush from the top
            edge, menu items reading around it like a real Mac. */}
        <div className="absolute left-1/2 top-0 z-50 origin-top -translate-x-1/2 scale-[0.55] sm:scale-[0.8] lg:scale-100">
          <VovyNotch theme={theme} flared />
        </div>

        {/* The pitch, straight on the wallpaper — no container.
            data-vovy-hero-content keeps VovyGuide's measurements working. */}
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center px-4 pb-28 pt-20 sm:px-6">
          <motion.div
            data-vovy-hero-content
            variants={heroContainer}
            initial="hidden"
            animate="show"
            className="pointer-events-auto w-full max-w-[900px] text-center"
          >
            {content ? (
              <motion.div variants={fadeUp} className="flex flex-col items-center">
                {content}
              </motion.div>
            ) : (
            <div className="flex flex-col items-center">
              <motion.h1
                variants={fadeUp}
                className="bg-clip-text font-black text-transparent text-[clamp(2.375rem,5.2vw,4.25rem)] leading-[1.04] tracking-[-0.015em]"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, #FFFFFF 38%, rgba(255,255,255,0.6) 100%)",
                }}
              >
                <span className="block">An AI expert</span>
                <span className="block">
                  that follows your{" "}
                  <span className="whitespace-nowrap">
                    cursor
                    <svg
                      viewBox="0 0 16 16"
                      aria-hidden="true"
                      className="ml-2 inline-block h-[0.5em] w-[0.5em] -rotate-6 align-baseline"
                    >
                      <path
                        d="M1 1l5.2 13.4 2.1-5.1 5.1-2.1z"
                        fill="#fff"
                        stroke="rgba(2,32,58,0.4)"
                        strokeWidth="1"
                      />
                    </svg>
                  </span>
                </span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mt-4 max-w-[46ch] text-balance text-[clamp(1rem,1.5vw,1.1875rem)] font-semibold leading-relaxed text-white"
              >
                It sees your screen, explains in plain English, or just does it
                for you.
              </motion.p>

              <motion.div variants={fadeUp} className="mt-6 flex w-full flex-col items-center">
                <div className="flex w-full justify-center sm:w-auto">
                <DownloadCta
                  placement="hero"
                  className="btn-gamey inline-flex w-full items-center justify-center gap-2.5 rounded-2xl bg-white px-8 py-4 text-[15px] font-extrabold text-[#1D1D1F] transition-colors hover:bg-slate-50 sm:w-auto sm:text-base"
                  iconClassName="w-4 h-4"
                  style={{
                    ["--gamey-shadow" as string]: "#0B4E8F",
                    ["--gamey-shadow-hover" as string]: "#083A6B",
                  }}
                />
                </div>
                <p className="mt-4 text-[12px] font-semibold text-white/85">
                  Free to start &middot; Apple Silicon &middot;{" "}
                  <WindowsWaitlistButton
                    className="inline bg-transparent font-semibold underline underline-offset-2 transition-colors hover:text-white"
                    iconClassName="hidden"
                  />{" "}
                  &middot;{" "}
                  <Link to="/terms-of-service" className="underline underline-offset-2 hover:text-white">
                    Terms
                  </Link>
                </p>
              </motion.div>
            </div>
            )}
          </motion.div>
        </div>

        {/* The dock: the AI tools Vovy works inside, with Vovy centered and
            a step larger — the star of its own dock. */}
        <div className="absolute inset-x-0 bottom-[14px] z-30 flex justify-center">
          <div
            className="flex items-end gap-[7px] rounded-[16px] border border-white/50 bg-white/35 px-[10px] py-[8px] shadow-[0_8px_24px_rgba(2,32,58,0.18)] backdrop-blur-md sm:gap-[9px] sm:rounded-[18px] sm:px-[12px]"
          >
            {DOCK_LEFT.map((app) => (
              <DockIcon key={app.label} app={app} />
            ))}
            <div
              title="Vovy"
              className="h-[42px] w-[42px] overflow-hidden rounded-[11px] shadow-[0_3px_8px_rgba(2,32,58,0.3)] sm:h-[50px] sm:w-[50px] sm:rounded-[13px]"
            >
              <img src="/images/vovy-appicon.png" alt="Vovy" className="h-full w-full object-cover" />
            </div>
            {DOCK_RIGHT.map((app) => (
              <DockIcon key={app.label} app={app} />
            ))}
          </div>
        </div>

        {/* The flying VovyGuide's resting spot, above the dock. */}
        <div
          data-vovy-hero-pad
          aria-hidden="true"
          className="absolute bottom-[84px] left-1/2 hidden h-[8rem] w-[16rem] -translate-x-1/2 md:block"
        />
      </motion.section>
    </div>
  );
};

// The vovy.ai home page. Built at /newdesktop, which now redirects here.
//
// The file keeps that name so its history stays in one place; the route is what
// makes it the home page, not the filename.

const heroContainer = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 160, damping: 18 } },
};
const NewDesktop = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, getUserAvatar } = useAuth();
  const avatarUrl = getUserAvatar();
  const [searchParams] = useSearchParams();
  const theme = searchParams.get("theme") === "dusk" ? ("dusk" as const) : ("light" as const);

  useEffect(() => {
    // warm the guide's .riv bytes so Vovy appears without a fetch wait
    // (same preload approach as VovyRive)
    fetch(VOVY_SRC, { credentials: "omit", priority: "high" as RequestPriority }).catch(() => {});
  }, []);

  // Scroll to the pricing section when arriving at /#pricing — where the footer
  // link and the /pricing redirect both land.
  useEffect(() => {
    if (location.hash !== "#pricing") return;
    let tries = 0;
    const scroll = () => {
      const el = document.getElementById("pricing");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else if (tries++ < 20) setTimeout(scroll, 200);
    };
    setTimeout(scroll, 300);
  }, [location.hash]);

  return (
    <div
      // overflow-x-CLIP, not -hidden: hidden makes this div a scroll
      // container, which silently kills the hero frame's position:sticky;
      // clip cuts overflow without doing that.
      className={`min-h-screen relative overflow-x-clip ${theme === "dusk" ? "dusk-page bg-[#1A1114]" : "bg-white"}`}
    >
      {/* no `noindex` — this is the site's canonical home page */}
      <SEOHead
        title="Vovy | The AI Technical Co-Founder That Follows Your Cursor"
        appendSiteName={false}
        description="Vovy follows your cursor, sees your screen when you ask, and builds with you in any app on your Mac. You talk. Vovy builds."
        canonical="https://www.vovy.ai/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_ITEMS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />

      <div className="relative z-10">
        <main>
          {/* ── Hero: a Mac that already runs Vovy (adrien.website/txt
              framing). The first view IS a desktop — menu bar with the real
               glyph and system font, the live notch where it lives, a glass
              panel carrying the pitch, and a dock with exactly one app. On
              scroll the desktop shrinks into a rounded frame and the page
              continues beneath it. Replaces the notch navbar entirely. */}
          {/* The new hero from /herotest: nav, headline and the animated
              MacBook with Vovy working in the notch. Replaced the cloud
              desktop hero (HeroDesktopFrame, still used on /desktop/installing). */}
          <HomeHero />

          {/* Integrations, right under the hero MacBook */}
          <section
            id="integrations"
            className={`scroll-mt-24 ${theme === "dusk" ? "bg-transparent" : "bg-white"} px-[20px] sm:px-6 lg:px-8 py-20 md:py-28`}
          >
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6 }}
                className="text-center max-w-2xl mx-auto"
              >
                {/* reserved landing pad — the flying VovyGuide parks here, so
                    nothing else may occupy the space */}
                {/* Sized to Vovy's LARGE box (154x179 under 768px, 256x294
                    above) — a smaller pad let him overlap the heading. */}
                <div
                  data-vovy-stop="integrations"
                  className="mx-auto mb-4 md:mb-10 h-[11.5rem] w-[9.75rem] md:h-[18.75rem] md:w-[16rem]"
                />
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-foreground">
                  Every AI tool. One Vovy.
                </h2>
              </motion.div>

              <div className="mt-16">
                <Suspense fallback={<div className="w-full h-32" />}>
                  <IntegrationsArc />
                </Suspense>
              </div>

              {/* From the tools to what Vovy does in them: the task library */}
              <div className="mt-12 flex justify-center">
                <Link
                  to="/tasks"
                  className="btn-gamey inline-flex items-center justify-center gap-2 rounded-2xl bg-[#0EA5E9] px-7 py-3.5 text-[15px] font-extrabold text-white transition-colors hover:bg-[#0284C7]"
                  style={{
                    ["--gamey-shadow" as string]: "#0369a1",
                    ["--gamey-shadow-hover" as string]: "#075985",
                  }}
                >
                  View Example Tasks
                  <ChevronRight className="size-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </section>

          {/* What Vovy does once it's installed — explain, then act — with the
              real notch UI standing in for the lesson clips on /learn */}
          <div id="features" className="scroll-mt-24">
            <Suspense fallback={<div className="h-96 bg-white" />}>
              <VovyDoesSection />
            </Suspense>
          </div>

          {/* Pricing — shared verbatim with /, /go and /learn; the landing pad
              hosts the rocket-riding Vovy */}
          <div id="pricing" className={`scroll-mt-24 ${theme === "dusk" ? "bg-transparent" : "bg-white"}`}>
            <PricingSection landingPad />
          </div>

          {/* FAQ — accordion list */}
          <section id="faq" className={`scroll-mt-24 ${theme === "dusk" ? "bg-transparent" : "bg-white"} px-[20px] sm:px-6 lg:px-8 py-16 md:py-24`}>
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6 }}
                className="text-center max-w-2xl mx-auto"
              >
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-foreground">
                  Frequently asked questions.
                </h2>
                <p className="mt-6 text-lg text-muted-foreground">
                  What to know about me, privacy, and getting started.
                </p>
              </motion.div>

              <div className="mt-12 max-w-3xl mx-auto space-y-3">
                {FAQ_ITEMS.map((item) => (
                  <Collapsible key={item.q}>
                    <CollapsibleTrigger className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-left transition-colors hover:bg-slate-50 data-[state=open]:rounded-b-none data-[state=open]:border-b-transparent">
                      <span className="text-base font-semibold text-slate-900">{item.q}</span>
                      <ChevronDown className="h-5 w-5 shrink-0 text-slate-400 transition-transform group-data-[state=open]:rotate-180" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="rounded-b-2xl border border-t-0 border-slate-200 bg-white px-5 pb-4">
                      <p className="text-base leading-relaxed text-slate-600">{item.a}</p>
                      {item.links && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {item.links.map(([label, href]) => (
                            <Link
                              key={href}
                              to={href}
                              className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-sm font-medium text-sky-700 transition-colors hover:bg-sky-100"
                            >
                              {label}
                              <ChevronRight className="h-3.5 w-3.5" />
                            </Link>
                          ))}
                        </div>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </div>
          </section>

          {/* Ease the white down into the footer blue.

              This can't be Tailwind's from/via/to: those are three stops, and
              stacking `via-` classes doesn't add more — each one overwrites the
              last, so the whole ramp was collapsing into the final 12% and
              landing on the footer as a hard band.

              The stops below are white → #2E92D6 sampled along a smoothstep
              curve, so the ramp is slowest at both ends. That's what kills the
              seam: it arrives at the footer's blue already almost there, rather
              than still moving. The extra stops also give the browser less
              distance to dither across, which keeps the banding down. */}
          <div
            aria-hidden="true"
            className="h-48 md:h-64"
            style={{
              backgroundImage:
                "linear-gradient(to bottom, #FFFFFF 0%, #F9FCFE 10%, #E9F4FB 20%, #D2E8F6 30%, #B5D9F1 40%, #97C9EB 50%, #78B8E4 60%, #5BAADF 70%, #449DDA 80%, #3495D7 90%, #2E92D6 100%)",
            }}
          />
        </main>

        {/* pb is a strip of the footer's own blue below the watermark logo,
            which the shared Footer otherwise runs flush to the page edge.
            Applied here rather than in Footer — that one is on every page. */}
        <div className="relative bg-[#2E92D6] pb-[10px]">
          <Suspense fallback={null}>
            <Footer />
          </Suspense>
        </div>
      </div>

      {/* the flying tour guide — chases the cursor in the hero, then flies
          down and parks on the integrations landing pad */}
      <VovyGuide stops={GUIDE_STOPS} />
    </div>
  );
};

export default NewDesktop;
